import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Button, Alert, Typography, Paper, Chip, IconButton,
  CircularProgress, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import LoginIcon from '@mui/icons-material/Login';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExtensionIcon from '@mui/icons-material/Extension';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import {
  ComposedChart, Area, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { superAdminAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { DashboardFilterBar } from '../../components/dashboard/DashboardFilterBar';
import { apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { DATE_PRESETS, getDateRangeFromPreset } from '../../constants/dashboardModules';
import { tableHeadRowSx, tableHeadCellSx, tableHeadTextSx, tableBodyCellSx } from '../../theme/tableStyles';

const CHART_COLORS = ['#1565c0', '#42a5f5', '#7cb342', '#29b6f6', '#f59e0b', '#8b5cf6'];

interface DashboardStats {
  totalCompanies: number;
  activeCompanies: number;
  pendingCompanies: number;
  suspendedCompanies?: number;
  totalPlans: number;
  newTenantsInRange?: number;
}

interface AnalyticsData {
  totalTenants?: number;
  activeTenants?: number;
  totalUsers?: number;
  monthlyRevenue?: number;
  loginCount?: number;
  growthPercent?: number;
  featureUsage?: Record<string, number>;
}

const PERIOD_PRESETS = DATE_PRESETS.filter((p) => p.days === 7 || p.days === 30 || p.days === 90);

const QUICK_ACTIONS = [
  { label: 'Pending Approvals', path: '/super-admin/tenants/pending', icon: <PendingActionsIcon />, color: '#f59e0b', desc: 'Review new signups' },
  { label: 'All Tenants', path: '/super-admin/tenants', icon: <BusinessIcon />, color: '#3b82f6', desc: 'Manage companies' },
  { label: 'Module Access', path: '/super-admin/tenant-modules', icon: <ViewModuleIcon />, color: '#1565c0', desc: 'Per-tenant modules' },
  { label: 'Custom CRM', path: '/super-admin/crm-modules', icon: <ExtensionIcon />, color: '#8b5cf6', desc: 'Leads, tasks & more' },
  { label: 'Report Templates', path: '/super-admin/report-templates', icon: <AssessmentIcon />, color: '#42a5f5', desc: 'Tenant reports' },
  { label: 'Report Access', path: '/super-admin/tenant-reports', icon: <ViewModuleIcon />, color: '#0ea5e9', desc: 'Assign to tenants' },
  { label: 'Plans & Billing', path: '/super-admin/plans', icon: <CardMembershipIcon />, color: '#06b6d4', desc: 'Subscription tiers' },
];

function KpiCard({
  title, value, subtitle, icon, gradient, trend,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  gradient: string;
  trend?: number;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${brandColors.border}`,
        background: '#fff',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: brandCss.shadowLg,
          borderColor: '#bbf7d0',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: gradient,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography fontSize={11} fontWeight={700} color="text.secondary" letterSpacing={0.8} textTransform="uppercase">
            {title}
          </Typography>
          <Typography fontWeight={800} fontSize="1.85rem" color={brandColors.textDark} lineHeight={1.2} mt={0.75}>
            {value}
          </Typography>
          {subtitle && (
            <Typography fontSize={12} color="text.secondary" mt={0.5}>{subtitle}</Typography>
          )}
          {trend !== undefined && (
            <Chip
              size="small"
              icon={<TrendingUpIcon sx={{ fontSize: '14px !important' }} />}
              label={`+${trend}% growth`}
              sx={{ mt: 1.5, height: 22, fontSize: 11, fontWeight: 700, bgcolor: brandCss.green100, color: brandCss.primaryDark }}
            />
          )}
        </Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 52, height: 52, borderRadius: 2.5, background: gradient, boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
          color: '#fff',
        }}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );
}

function ChartPanel({ title, subtitle, children, action }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${brandColors.border}`,
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography fontWeight={800} fontSize={15} color={brandCss.primaryDark}>{title}</Typography>
          {subtitle && <Typography fontSize={12} color="text.secondary">{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function statusChip(status: string) {
  const s = String(status || '').toLowerCase();
  const map: Record<string, { bg: string; color: string }> = {
    active: { bg: brandCss.green100, color: brandCss.primaryDark },
    paid: { bg: brandCss.green100, color: brandCss.primaryDark },
    pending: { bg: '#fef3c7', color: '#d97706' },
    suspended: { bg: '#fee2e2', color: '#dc2626' },
  };
  const style = map[s] || map.pending;
  return (
    <Chip label={status || '—'} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 11, textTransform: 'capitalize' }} />
  );
}

export const SuperAdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({ totalCompanies: 0, activeCompanies: 0, pendingCompanies: 0, totalPlans: 0 });
  const [analytics, setAnalytics] = useState<AnalyticsData>({});
  const [tenants, setTenants] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<any[]>([]);
  const [tenantStatusChart, setTenantStatusChart] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [datePreset, setDatePreset] = useState(30);
  const [fromDate, setFromDate] = useState(() => getDateRangeFromPreset(30).fromDate);
  const [toDate, setToDate] = useState(() => getDateRangeFromPreset(30).toDate);

  const navigate = useNavigate();
  const { formatCurrency, formatDate } = useLocale();

  useEffect(() => {
    const range = getDateRangeFromPreset(datePreset);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  }, [datePreset]);

  const buildParams = useCallback(() => {
    const params: Record<string, string> = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return params;
  }, [fromDate, toDate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await superAdminAPI.getDashboard(buildParams());
      const data = res.data;
      setStats(data.stats || {});
      setAnalytics(data.analytics || {});
      setTenants(data.recentTenants || []);
      setTransactions(data.recentTransactions || []);
      setRevenueTrend(data.revenueTrend || []);
      setTenantStatusChart(data.tenantStatusChart || []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (days: number) => {
    setDatePreset(days);
    const range = getDateRangeFromPreset(days);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  };

  const clearFilters = () => {
    setDatePreset(30);
    const range = getDateRangeFromPreset(30);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  };

  const periodLabel = useMemo(
    () => PERIOD_PRESETS.find((p) => p.days === datePreset)?.label || 'Last 30 days',
    [datePreset],
  );

  const featureUsageData = useMemo(() =>
    Object.entries(analytics.featureUsage || {}).map(([name, value]) => ({
      name: name.toUpperCase(),
      usage: value as number,
    })), [analytics.featureUsage]);

  if (loading && !stats.totalCompanies && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Dashboard"
        subtitle={getPageSubtitle('super_admin_dashboard')}
        icon={<SpaceDashboardIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {stats.pendingCompanies > 0 && (
              <Button
                size="small"
                variant="contained"
                startIcon={<PendingActionsIcon />}
                onClick={() => navigate('/super-admin/tenants/pending')}
                sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, fontWeight: 700 }}
              >
                {stats.pendingCompanies} pending
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={load} disabled={loading} size="small">
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <DashboardFilterBar
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={(v) => { setFromDate(v); setDatePreset(-1); }}
        onToDateChange={(v) => { setToDate(v); setDatePreset(-1); }}
        datePresets={PERIOD_PRESETS.map((p) => ({ id: String(p.days), label: p.label, days: p.days }))}
        activePresetId={PERIOD_PRESETS.some((p) => p.days === datePreset) ? String(datePreset) : 'custom'}
        onPresetClick={(p) => { if (p.days != null) applyPreset(p.days); }}
        onApply={load}
        onReset={clearFilters}
        loading={loading}
        activeFilterCount={PERIOD_PRESETS.some((p) => p.days === datePreset) ? 0 : 1}
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {stats.pendingCompanies > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, border: '1px solid #fde68a' }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/super-admin/tenants/pending')}>
              Review now
            </Button>
          }
        >
          <strong>{stats.pendingCompanies} tenant(s)</strong> waiting for approval.
        </Alert>
      )}

      {/* KPI row */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, mb: 2 }}>
          <CircularProgress size={22} color="success" />
        </Box>
      )}
      <Grid container spacing={2.5} sx={{ mb: 3, opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Total Tenants"
            value={stats.totalCompanies}
            subtitle={`${stats.newTenantsInRange ?? 0} new · ${periodLabel}`}
            icon={<BusinessIcon sx={{ color: '#fff' }} />}
            gradient="linear-gradient(135deg, #3b82f6, #2563eb)"
            trend={analytics.growthPercent}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Active Tenants"
            value={stats.activeCompanies}
            subtitle={`${Math.round((stats.activeCompanies / Math.max(stats.totalCompanies, 1)) * 100)}% active`}
            icon={<CheckCircleIcon sx={{ color: '#fff' }} />}
            gradient={brandCss.gradientPrimary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Platform Users"
            value={analytics.totalUsers || 0}
            subtitle={`${analytics.loginCount || 0} logins · ${periodLabel}`}
            icon={<PeopleIcon sx={{ color: '#fff' }} />}
            gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Revenue"
            value={formatCurrency(analytics.monthlyRevenue || 0)}
            
            icon={<AttachMoneyIcon sx={{ color: '#fff' }} />}
            gradient="linear-gradient(135deg, #f59e0b, #d97706)"
          />
        </Grid>
      </Grid>

      {/* Quick actions */}
      <Typography fontWeight={800} fontSize={13} color={brandCss.primaryDark} mb={1.5} letterSpacing={0.5}>
        QUICK ACTIONS
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {QUICK_ACTIONS.map((action) => (
          <Grid item xs={6} sm={4} md={2} key={action.path}>
            <Paper
              elevation={0}
              onClick={() => navigate(action.path)}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${brandColors.border}`,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: `0 8px 24px ${action.color}22`,
                  borderColor: action.color,
                },
              }}
            >
              <Box sx={{
                width: 44, height: 44, mx: 'auto', mb: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%', bgcolor: `${action.color}18`, color: action.color,
              }}>
                {action.icon}
              </Box>
              <Typography fontWeight={700} fontSize={12}>{action.label}</Typography>
              <Typography fontSize={10} color="text.secondary">{action.desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Charts row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <ChartPanel title="Revenue & Tenant Growth" >
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={revenueTrend}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1565c0" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#1565c0" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${brandColors.border}` }} />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue (₹)" stroke="#1565c0" fill="url(#revGrad)" strokeWidth={2.5} />
                <Bar yAxisId="right" dataKey="tenants" name="Tenants" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={18} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartPanel title="Tenant Status" subtitle="Active, pending, suspended">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={tenantStatusChart}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {tenantStatusChart.map((entry, i) => (
                    <Cell key={entry.name} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Feature Adoption" subtitle="Usage across tenant base (%)">
            {featureUsageData.map((item) => (
              <Box key={item.name} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography fontSize={13} fontWeight={600}>{item.name}</Typography>
                  <Typography fontSize={13} fontWeight={700} color={brandCss.primaryDark}>{item.usage}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.usage}
                  sx={{
                    height: 10, borderRadius: 5, bgcolor: brandCss.green100,
                    '& .MuiLinearProgress-bar': { borderRadius: 5, background: brandCss.gradientPrimary },
                  }}
                />
              </Box>
            ))}
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Login Activity" subtitle="Platform engagement snapshot">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: brandCss.green50, border: `1px solid ${brandCss.green200}` }}>
                  <LoginIcon sx={{ color: brandCss.primary, mb: 1 }} />
                  <Typography fontWeight={800} fontSize="1.5rem">{analytics.loginCount || 0}</Typography>
                  <Typography fontSize={12} color="text.secondary">Logins in range</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <CardMembershipIcon sx={{ color: brandColors.info, mb: 1 }} />
                  <Typography fontWeight={800} fontSize="1.5rem">{stats.totalPlans}</Typography>
                  <Typography fontSize={12} color="text.secondary">Active plans</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fef3c7', border: '1px solid #fde68a' }}>
                  <PendingActionsIcon sx={{ color: '#d97706', mb: 1 }} />
                  <Typography fontWeight={800} fontSize="1.5rem">{stats.pendingCompanies}</Typography>
                  <Typography fontSize={12} color="text.secondary">Pending approval</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f3ff', border: '1px solid #ddd6fe' }}>
                  <TrendingUpIcon sx={{ color: '#7c3aed', mb: 1 }} />
                  <Typography fontWeight={800} fontSize="1.5rem">+{analytics.growthPercent || 0}%</Typography>
                  <Typography fontSize={12} color="text.secondary">Growth rate</Typography>
                </Paper>
              </Grid>
            </Grid>
          </ChartPanel>
        </Grid>
      </Grid>

      {/* Tables */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <ChartPanel
            title="Recent Tenants"
            subtitle={`${tenants.length} tenant(s) · ${periodLabel}`}
            action={
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/super-admin/tenants')} sx={{ fontWeight: 600 }}>
                View all
              </Button>
            }
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Company</Typography></TableCell>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Plan</Typography></TableCell>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Users</Typography></TableCell>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Status</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((t) => (
                  <TableRow
                    key={t._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/super-admin/tenants')}
                  >
                    <TableCell sx={tableBodyCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box>
                          <Typography fontWeight={700} fontSize={13}>{t.companyName}</Typography>
                          <Typography fontSize={11} color="text.secondary">{t.email || t.contactEmail || '—'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Typography fontSize={13}>{t.subscriptionPlan?.planName || t.subscription?.planName || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>{t.usage?.users ?? '—'}</TableCell>
                    <TableCell sx={tableBodyCellSx}>{statusChip(t.status)}</TableCell>
                  </TableRow>
                ))}
                {!tenants.length && (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No tenants in this period</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={5}>
          <ChartPanel
            title="Recent Transactions"
            subtitle={`${transactions.length} payment(s) · ${periodLabel}`}
            action={
              <Button size="small" endIcon={<ReceiptLongIcon />} onClick={() => navigate('/super-admin/transactions')} sx={{ fontWeight: 600 }}>
                All
              </Button>
            }
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Tenant</Typography></TableCell>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Amount</Typography></TableCell>
                  <TableCell sx={tableHeadCellSx()}><Typography sx={tableHeadTextSx}>Status</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn._id} hover>
                    <TableCell sx={tableBodyCellSx}>
                      <Typography fontWeight={600} fontSize={13}>{txn.companyName || txn.tenant}</Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {txn.paidAt || txn.date ? formatDate(String(txn.paidAt || txn.date)) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Typography fontWeight={700} fontSize={13} color={brandCss.primaryDark}>
                        {formatCurrency(Number(txn.amount) || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>{statusChip(txn.status)}</TableCell>
                  </TableRow>
                ))}
                {!transactions.length && (
                  <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No transactions in this period</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </ChartPanel>
        </Grid>
      </Grid>
    </Box>
  );
};
